import { useMemo, useState } from 'react'
import { errorHandler } from '../../../../../../services/util/error.service'
import { convertVolumeAndUnitsToQuantity } from '../../../../../../services/util/volume-and-units.service'
import * as ResumoHttp from '../../../../../../services/api/https/picking/resumo-separcao.http'

export const LOAD_MESSAGE = {
  NONE: 'NONE',
  SAVING: 'Salvando desdobramento...',
  SUCCESS: 'Desdobramento salvo com sucesso',
}

const INITIAL_STATE = {
  falta: null,
  // quantidade atual da falta original (decrementada a cada desdobramento criado)
  qteOriginalAtual: 0,
  // missing_reason da falta original (pode ser alterado pelo usuário)
  ocorrenciaOriginal: null,
  // campos do formulário de novo desdobramento
  novoVol: '',
  novoFrac: '',
  novaOcorrencia: null,
  desdobramentos: [],
  loadingMessage: LOAD_MESSAGE.NONE,
  errorMessage: null,
}

function convertToQte(vol, frac, falta) {
  const volume = Number(vol) || 0
  const units = Number(frac) || 0
  return convertVolumeAndUnitsToQuantity({
    volume,
    units,
    unitSku: falta.unit_sku,
    caixaComPesoVariavel: falta.caixa_com_peso_variavel,
  })
}

export default function useModalDesdobramentoFalta() {
  const [state, setState] = useState(INITIAL_STATE)

  const localDispatch = useMemo(() => {
    return {
      onOpen(falta) {
        setState({
          ...INITIAL_STATE,
          falta,
          qteOriginalAtual:
            falta.qte_missing_adjusted !== undefined
              ? falta.qte_missing_adjusted
              : falta.qte_missing,
          ocorrenciaOriginal: falta.missing_reason || null,
          novoVol: '',
          novoFrac: '',
          novaOcorrencia: null,
          desdobramentos: [],
          loadingMessage: LOAD_MESSAGE.NONE,
          errorMessage: null,
        })
      },

      onOcorrenciaOriginalChange(value) {
        setState(prev => ({ ...prev, ocorrenciaOriginal: value }))
      },

      onNovoVolChange(value) {
        setState(prev => ({ ...prev, novoVol: value }))
      },

      onNovoFracChange(value) {
        setState(prev => ({ ...prev, novoFrac: value }))
      },

      onNovaOcorrenciaChange(value) {
        setState(prev => ({ ...prev, novaOcorrencia: value }))
      },

      onDesdobrar() {
        setState(prev => {
          const { falta, qteOriginalAtual, novoVol, novoFrac, novaOcorrencia, desdobramentos } =
            prev

          if (!novaOcorrencia) {
            return { ...prev, errorMessage: 'Selecione uma Ocorrência para o desdobramento.' }
          }

          const novaQte = convertToQte(novoVol, novoFrac, falta)

          if (novaQte === 0) {
            return { ...prev, errorMessage: 'Informe uma quantidade maior que zero.' }
          }

          // qte_missing é negativo; novaQte deverá ser negativo também
          const novaQteNeg = -Math.abs(novaQte)
          const novaQteOriginal = qteOriginalAtual - novaQteNeg // subtrai o negativo = diminui

          // A falta original não pode zerar ou ficar positiva
          if (novaQteOriginal >= 0) {
            return {
              ...prev,
              errorMessage:
                'A quantidade do desdobramento não pode ser maior ou igual à quantidade restante da Falta Original.',
            }
          }

          const novoDesdobramento = {
            vol: Number(novoVol) || 0,
            frac: Number(novoFrac) || 0,
            qte_missing: novaQteNeg,
            missing_reason: novaOcorrencia,
          }

          return {
            ...prev,
            qteOriginalAtual: novaQteOriginal,
            desdobramentos: [...desdobramentos, novoDesdobramento],
            novoVol: '',
            novoFrac: '',
            novaOcorrencia: null,
            errorMessage: null,
          }
        })
      },

      onRemoverDesdobramento(index) {
        setState(prev => {
          const desdobramentos = [...prev.desdobramentos]
          const removed = desdobramentos.splice(index, 1)[0]
          // restaura a quantidade removida de volta para a falta original
          const novaQteOriginal = prev.qteOriginalAtual + removed.qte_missing
          return { ...prev, desdobramentos, qteOriginalAtual: novaQteOriginal }
        })
      },

      onSalvar() {
        setState(prev => {
          const { ocorrenciaOriginal, desdobramentos } = prev

          if (!ocorrenciaOriginal) {
            return { ...prev, errorMessage: 'A Falta Original deve ter uma Ocorrência associada.' }
          }

          const semOcorrencia = desdobramentos.findIndex(d => !d.missing_reason)
          if (semOcorrencia !== -1) {
            return {
              ...prev,
              errorMessage: `O Desdobramento ${semOcorrencia + 1} deve ter uma Ocorrência associada.`,
            }
          }

          return { ...prev, loadingMessage: LOAD_MESSAGE.SAVING }
        })
      },

      async executeSave(falta_id, ocorrenciaOriginal, desdobramentos) {
        try {
          const payload = desdobramentos.map(d => ({
            qte_missing: d.qte_missing,
            missing_reason_id: d.missing_reason.id,
          }))

          await ResumoHttp.desdobrarFalta(falta_id, ocorrenciaOriginal.id, payload)

          setState(prev => ({ ...prev, loadingMessage: LOAD_MESSAGE.SUCCESS }))
        } catch (error) {
          setState(prev => ({
            ...prev,
            loadingMessage: LOAD_MESSAGE.NONE,
            errorMessage: errorHandler(error),
          }))
        }
      },

      resetState() {
        setState(prev => ({
          ...prev,
          loadingMessage: LOAD_MESSAGE.NONE,
          errorMessage: null,
        }))
      },
    }
  }, [])

  return { state, localDispatch }
}
